<template>
  <div :class="['pc__wrapper', {'collection': showOptionDetails}, {'atcDetails': showATCDetails}]">
    <a :href="`/products/${product.handle}`" class="pc__images">
      <div v-if="isFeatureTag" class="pc__images-tag" :style=" { '--bg-tag': isFeatureTag.color }">
        {{ isFeatureTag.name }}
      </div>
      <transition name="fade" mode="out-in">
        <ImageField
          class="pc__feature-img"
          :key="featureImages.length ? featureImages[0].src : selectedVariant.featured_image" 
          :obj="{  
            'url': featureImages.length ? featureImages[0].src : selectedVariant.featured_image, 
            'alt': featureImages.length ? featureImages[0].alt : 'feature image', 
            'width': 656, 
            'height': 618 
          }"
        />
      </transition>
    </a>
    <div class="pc__info">
      <div class="pc__info-inner">
        <div class="pc__title-price">
          <span>
            {{ product.title || product.product_title }}
          </span>
          <div class="pc__price">
            <s v-if="selectedVariant.compare_at_price > 0">
              <span class="mobile-text-base">{{ formatPrice(selectedVariant.compare_at_price) }}</span>
            </s>
            <span class="mobile-text-base">{{ formatPrice(selectedVariant.price) }}</span>
          </div>
        </div>
        <div class="pc__variant-title text-sm" :class="{'mobile-text-base' : !showOptionDetails}">
          {{ variantTitle }}
        </div>
      </div>
      <button v-if="!showOptionDetails" @click="onATC()" class="btn btn--primary btn--solid pc__atc" :disabled="!selectedVariant.available || selectedVariant.sold_out">
        Add to Bag
      </button>
      <button v-else @click="onATCCollection(true)" class="btn btn--primary btn--solid pc__atc">
        Add to Bag
      </button>
    </div>
    <div v-if="showATCDetails" @click="onATCCollection(false)" class="pc__option-overlay"></div>
    <div v-if="showATCDetails" class="pc__option-wrapper">
      <div class="pc__title-wrapper">
        <h2 class="text-2xl">{{ product.title || product.product_title }}</h2>
        <button @click="onATCCollection(false)" class="pc__option-close">
          <svg class="icon icon--xs">
            <use href="#exit"></use>
          </svg>
        </button>
      </div>
      <BuyboxOptions
        :product="product"
        :selectedVariant="selectedVariant"
        :isDropdown="false"
        @update-option="onUpdateOption"
      />
      <BuyboxSubscription 
        ref="buyboxSubscription"
        :product="product"
        :selectedVariant="selectedVariant"
        :isMultiPack="isMultiPack"
        :selectedSellingPlan="selectedSellingPlan"
        @update-option="onUpdateOption"
        @update-selling-plan="onUpdateSellingPlan"
        @update-is-subscription="onUpdateIsSubscription"
      />
      <BuyboxATC
        :isMultiPack="isMultiPack"
        :isSubscription="buyboxSubscription?.isSubscription"
        :selectedVariant="selectedVariant"
        :selectedSellingPlan="selectedSellingPlan"
        @toggle-option-drawer="toggleOptionDrawer"
      />
      <a :href="`/products/${product.handle}`" class="pc__details mobile-text-base text-sm">View full details</a>
    </div>
  </div>
</template>

<script setup>
  import { computed, toRefs, ref, onBeforeMount } from 'vue';
  import ImageField from '../global/image.vue';
  import BuyboxOptions from '../buybox/components/buybox-options.vue';
  import BuyboxSubscription from '../buybox/components/buybox-subscription.vue';
  import BuyboxATC from "../buybox/components/buybox-atc.vue";

  const props = defineProps({
    product: {
      type: Object,
      default: () => ({})
    },

    showOptionDetails: {
      type: Boolean,
      default: false
    }
  })

  const { product, showOptionDetails } = toRefs(props);
  const multiPackOptions = ['boxes', 'tubs', 'bottles'];
  const showATCDetails = ref(false);
  const selectedVariant = ref(null);
  const selectedSellingPlan = ref(null);
  const buyboxSubscription = ref(null);

  onBeforeMount(() => {
    initialSetup();
  })

  const isMultiPack = computed(() => {
    return product.value.options.some(option => multiPackOptions.includes(option.name.toLowerCase()));
  });

  const featureImages = computed(() => {
    const { images, options } = product.value;
    
    const firstOption = (options.find(option => option.position === 1) || null);
    const secondOption = (options.find(option => option.position === 2) || null);
    const imgPattern = imagePattern(firstOption, secondOption);
    const matchingImages = images.filter(image => imgPattern.test(image.alt));
    const imagesWithLast = matchingImages.filter(image => /\/last$/i.test(image.alt));
    const imagesWithoutLast = matchingImages.filter(image => !/\/last$/i.test(image.alt));

    return [...imagesWithoutLast, ...imagesWithLast];
  });

  const isFeatureTag = computed(() => {
    return selectedVariant.value?.feature_tag;
  })

  const variantTitle = computed(() => {
    const { options } = product.value || {};
    let title = '';

    options?.forEach((option, index) => {
      const optionName = option.name.toLowerCase();
      const isFlavorOption = optionName.includes('flavor');
      const isSizeOption = optionName.includes('size');

      if (isFlavorOption) {
        const flavorCount = option.values.length;
        if (flavorCount === 1) {
          if(option.values[0] !== 'Unflavored') {
            title += '1 flavor';
          }   
        } else {
          title += `${flavorCount} flavors`;
        }
      } else if (isSizeOption) {
        const servingText = `${option.selected} servings`;
        if (option.position === 1 || (options[0].values[0] === 'Unflavored')) {
          title += servingText;
        } else {
          title += ` / ${servingText}`;
        }
      }
    });

    return title.trim();
  });

  const initialSetup = () => {
    const { options, variants } = product.value;

    if (isMultiPack.value) {
      selectedVariant.value = variants.find(
        (variant) => variant.options[options.length - 1] === 'One Time Purchase'
      ) || variants[0];
    } else {
      selectedVariant.value = variants[0];
    }

    if(selectedVariant.value.has_subscription_plan){
      selectedSellingPlan.value = {
        name: selectedVariant.value.selling_plans[0].selling_plan.name,
        id: selectedVariant.value.selling_plans[0].selling_plan.id
      }
    }

    product.value.options.forEach((option, index) => {
      option.selected = selectedVariant.value.options[index];
    });
  }

  const updateSelectedVariant = (index) => {
    const { options, variants } = product.value;

    const selectedOptions = options.map(option => option.selected.toLowerCase());

    selectedVariant.value = variants.find(variant => {
      return variant.options.every((option, idx) => option.toLowerCase() === selectedOptions[idx]);
    });

    if(!selectedVariant.value && isMultiPack.value && options.length === 3) {
      const availableVariants = variants.filter(variant => {
        return variant.options[0] === options[0].selected && variant.options[1] === options[1].selected
      });

      if (availableVariants.length) {
        selectedVariant.value = availableVariants[0];
        options.forEach((option, index) => {
          option.selected = selectedVariant.value.options[index];
        });
      }
    }

    if(!selectedVariant.value.has_subscription_plan && buyboxSubscription.value) {
      buyboxSubscription.value.isSubscription = false;
    }
  };

  const onUpdateOption = (key,value, index) => {
    let tempSelectedOption = product.value.options[index].selected;
    product.value.options[index].selected = value;
    updateSelectedVariant(index);

    setTimeout(() => {
      if(key.toLowerCase() === 'size' && value == '20' && tempSelectedOption != value && buyboxSubscription.value) {
        buyboxSubscription.value.onSubscriptionChange(true)
      }
    })
  };

  const onUpdateSellingPlan = (value, isSubscription) => {
    selectedSellingPlan.value = value;

    if(isSubscription && buyboxSubscription.value) {
      buyboxSubscription.value.isSubscription = true;
    } 
  }

  const onUpdateIsSubscription = (value) => {
    if(buyboxSubscription.value) {
      buyboxSubscription.value.isSubscription = value;
    } 
  }

  const imagePattern = (firstOption, secondOption) => {
    const firstOptionLower = firstOption.selected.toLowerCase();
    const secondOptionLower = secondOption?.selected.toLowerCase();
    const commonPatterns = [`^${firstOptionLower}/all$`, `^${firstOptionLower}/all/last$`, `^all$`];

    const regexPattern = secondOption && !['boxes', 'tubs', 'bottles'].includes(secondOption.name.toLowerCase())
    ? [`^${firstOptionLower}/${secondOptionLower}$`, `^${firstOptionLower}/${secondOptionLower}/last$`, ...commonPatterns]
    : [`^${firstOptionLower}$`, `^${firstOptionLower}/last$`, ...commonPatterns];

    return new RegExp(regexPattern.join('|'), 'i');
  }

  const formatPrice = (price) => {
    return window.formattedPrice(price/100)
  }

  const onATC = async() => {
    window.openCart();
    await window.Additem({
      id: selectedVariant.value.id,
      quantity:1,
      ...(selectedSellingPlan.value) && {selling_plan: selectedSellingPlan.value.id}
    })
  }

  const onATCCollection = (value) => {
    let bodyStyle = document.body.style;
    showATCDetails.value = value;
    
    if(value) {
      bodyStyle.overflow = 'hidden' 
    }else {
      bodyStyle.overflow = 'auto' 
    }
  }

  const toggleOptionDrawer = () => {
    showATCDetails.value = false;
  }
</script>